import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Panel } from "../components/common";
import { scoreMeta, fmt, pct, chgColor, classColors } from "../lib/format";
import { LayoutGrid } from "lucide-react";

const EU = new Set(["DE", "FR", "IT", "ES", "EU", "GB"]);

// group key resolver + display order
const GROUPS = [
  ["us_equity", "US Equities"],
  ["us_etf", "US ETFs / Index"],
  ["eu_index", "European Indices"],
  ["forex", "Forex"],
  ["commodity", "Commodities"],
  ["crypto", "Crypto"],
  ["volatility", "Volatility"],
];

function groupOf(a) {
  if (a.asset_class === "equity") return "us_equity";
  if (a.asset_class === "etf_equity") return EU.has(a.country) ? "eu_index" : "us_etf";
  if (a.asset_class === "forex") return "forex";
  if (a.asset_class === "commodity") return "commodity";
  if (a.asset_class === "crypto") return "crypto";
  if (a.asset_class === "volatility") return "volatility";
  return "us_etf";
}

function Row({ a, onClick }) {
  const m = scoreMeta(a.smart_quant_score);
  return (
    <button data-testid={`market-row-${a.symbol}`} onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-[#141C2E] transition-colors text-left border border-transparent hover:border-[#1E293B]">
      <span className="font-mono font-bold text-sm w-16 shrink-0">{a.symbol}</span>
      <span className="text-xs text-slate-400 truncate flex-1">{a.name}</span>
      <span className="font-mono text-xs tnum w-16 text-right" style={{ color: chgColor(a.change_1d) }}>{pct(a.change_1d)}</span>
      <span className="font-mono text-xs font-bold tnum w-12 text-right px-1.5 py-0.5 rounded"
        style={{ color: m.color, background: `${m.color}18` }}>{fmt(a.smart_quant_score, 0)}</span>
    </button>
  );
}

export default function Markets() {
  const [assets, setAssets] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/assets").then((r) => setAssets(r.data)).catch(() => {});
  }, []);

  const byGroup = {};
  assets.forEach((a) => {
    const g = groupOf(a);
    (byGroup[g] = byGroup[g] || []).push(a);
  });
  Object.values(byGroup).forEach((arr) => arr.sort((x, y) => y.smart_quant_score - x.smart_quant_score));

  return (
    <div className="space-y-5" data-testid="markets-page">
      <div>
        <div className="eyebrow">browse the universe</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Markets Directory</h1>
        <p className="text-sm text-slate-400 mt-1">Sfoglia tutti gli asset per categoria e apri la scheda con un clic.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {GROUPS.filter(([g]) => byGroup[g]?.length).map(([g, label]) => (
          <Panel key={g} title={`${label} · ${byGroup[g].length}`} icon={<LayoutGrid size={13} />}
            right={<span className="h-2.5 w-2.5 rounded-full" style={{ background: classColors[byGroup[g][0].asset_class] }} />}>
            <div className="space-y-0.5" data-testid={`market-group-${g}`}>
              {byGroup[g].map((a) => <Row key={a.symbol} a={a} onClick={() => nav(`/asset/${a.symbol}`)} />)}
            </div>
          </Panel>
        ))}
      </div>
    </div>
  );
}
